import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as L from 'leaflet';
import { LatLngExpression } from 'leaflet';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Marker, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';

import LeafletMap from 'src/common/components/LeafletMap';
import { createCompetitionDotMarker } from 'src/common/components/leaflet/createDivIconMarker';
import { LanguageKeys } from 'src/common/constants/languages';
import { buildMarkersLatLngBounds } from 'src/helpers/checkinMapBounds';
import { getCompetitionMarkerColor } from 'src/helpers/competitionMarkerColors';
import { useEditionCheckinMap } from 'src/hooks/useEditionCheckinMap';
import { ICompetition } from 'src/interfaces/Competition';

import 'react-leaflet-cluster/dist/assets/MarkerCluster.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.Default.css';

const DEFAULT_CENTER: LatLngExpression = [46.5, 25.5];
const DEFAULT_ZOOM = 7;

function MapFitBounds({ markers }: { markers: { lat: number; lng: number }[] }) {
  const map = useMap();
  const signatureRef = useRef('');

  useEffect(() => {
    if (!markers.length) return;
    const signature = markers.map(m => `${m.lat},${m.lng}`).join('|');
    if (signature === signatureRef.current) return;
    signatureRef.current = signature;

    const bounds = buildMarkersLatLngBounds(markers);
    if (!bounds) return;
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
  }, [map, markers]);

  return null;
}

interface Props {
  editionId: string;
  editionYear: number;
  hasResultsData: boolean;
  competitions: ICompetition[];
  currentLang: LanguageKeys;
}

export default function ParticipantsDistributionMap({
  editionId,
  editionYear,
  hasResultsData,
  competitions,
  currentLang,
}: Props) {
  const { t } = useTranslation();
  const { markers, isLoading, error, totalCheckins, missingLocationCount } = useEditionCheckinMap(
    editionId,
    competitions,
  );

  const competitionIdsInOrder = useMemo(() => competitions.map(c => c.id), [competitions]);

  const markerIcons = useMemo(() => {
    const cache = new Map<string, L.DivIcon>();
    competitionIdsInOrder.forEach(id => {
      cache.set(id, createCompetitionDotMarker(getCompetitionMarkerColor(id, competitionIdsInOrder)));
    });
    return cache;
  }, [competitionIdsInOrder]);

  const showEmpty =
    !isLoading && !error && totalCheckins === 0 && !hasResultsData;
  const showUnavailableForEdition =
    !isLoading &&
    !error &&
    markers.length === 0 &&
    (totalCheckins > 0 || hasResultsData);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
        {t('race.results.mapTitle')}
      </Typography>

      {isLoading ? (
        <Typography variant="body2" color="text.secondary">
          {t('race.results.mapLoading')}
        </Typography>
      ) : null}

      {error ? (
        <Typography variant="body2" color="error">
          {t('race.results.mapError')}
        </Typography>
      ) : null}

      {showEmpty ? (
        <Typography variant="body2" color="text.secondary">
          {t('race.results.mapEmpty')}
        </Typography>
      ) : null}

      {showUnavailableForEdition ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('race.results.mapUnavailableForEdition', { year: editionYear })}
        </Typography>
      ) : null}

      {!error && markers.length > 0 ? (
        <LeafletMap
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          height="360px"
          scrollWheelZoom
        >
          {markers.length > 0 ? <MapFitBounds markers={markers} /> : null}
          <MarkerClusterGroup chunkedLoading>
            {markers.map(marker => (
              <Marker
                key={marker.id}
                position={[marker.lat, marker.lng]}
                icon={markerIcons.get(marker.competitionId) ?? createCompetitionDotMarker(marker.color)}
              />
            ))}
          </MarkerClusterGroup>
        </LeafletMap>
      ) : null}

      {missingLocationCount > 0 && markers.length > 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {t('race.results.mapMissingLocationCount', { count: missingLocationCount })}
        </Typography>
      ) : null}

      {competitions.length > 0 ? (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {t('race.results.mapLegendTitle')}
          </Typography>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
            {competitions.map(c => {
              const color = getCompetitionMarkerColor(c.id, competitionIdsInOrder);
              return (
                <Stack key={c.id} direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: color,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                  <Typography variant="caption">{c.i18n[currentLang]}</Typography>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}
